import { PractitionerEntityType } from "../../../../../../lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useState } from "react";
import { validateEmail } from "@/dentlab/src/hooks/useForm/form-field-validation-functions";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { Button } from "@/dentlab/src/components/ui/button";
import { nameTitleOptions } from "@/dentlab/src/lib/constants/nameTitle";
import { CustomSelectShadcn } from "@/dentlab/src/components/custom-select-shadcn.component";
import { CustomTextFieldShadcn } from "../../../form-input-components/custom-input-components/custom-text-field-shadcn.component";

interface PractitionerFormPropsInsert {
	clientId: string;
	practitioner?: undefined;
	onClose: () => void;
	jobId?: number;
	onSave?: (practitioner: PractitionerEntityType) => void;
}

interface PractitionerFormPropsUpdate {
	practitioner: PractitionerEntityType;
	clientId?: string;
	onClose?: () => void;
	onSave?: (practitioner: PractitionerEntityType) => void;
	jobId?: number;
}

export type PractitionerFormProps =
	| PractitionerFormPropsInsert
	| PractitionerFormPropsUpdate;

export const PractitionerForm: React.FC<PractitionerFormProps> = ({
	clientId = undefined,
	practitioner = undefined,
	jobId = undefined,
	onClose,
	onSave = () => {},
}) => {
	const { createPractitioner, updatePractitioner } = useCentralStore(
		(state) => ({
			createPractitioner: state.createPractitioner,
			updatePractitioner: state.updatePractitioner,
		})
	);

	const isUpdate = clientId ? false : true;
	const [formData, setFormData] = useState<PractitionerEntityType>({
		title: practitioner?.title ?? "",
		first_name: practitioner?.first_name ?? "",
		last_name: practitioner?.last_name ?? "",
		email: practitioner?.email ?? "",
		client_id: practitioner?.client_id ?? clientId ?? "",
		organization_id: practitioner?.organization_id ?? "",
		profile_id: practitioner?.profile_id ?? "",
		created_at: practitioner?.created_at ?? "",
		modified_at: practitioner?.modified_at ?? "",
		id: practitioner?.id ?? 0,
	});
	const [formErrors, setFormErrors] = useState<Record<string, string | null>>(
		{
			title: null,
			first_name: null,
			last_name: null,
			email: null,
		}
	);

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		// Validate email only if it's provided
		if (formData.email && !validateEmail(formData.email)) {
			setFormErrors({
				email: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
			});
			showNotification({
				message: "Bitte geben Sie eine gültige E-Mail-Adresse ein.",
				type: "error",
			});
			return;
		}

		// Proceed with creation/update
		if (isUpdate) {
			await updatePractitioner(formData);
		} else {
			if (jobId) {
				await createPractitioner(formData, jobId);
			} else {
				await createPractitioner(formData);
			}
		}
		if (onSave) {
			onSave(formData);
		}
		if (onClose) {
			onClose();
		}
	};

	return (
		<form onSubmit={onSubmit}>
			<div className="flex flex-col gap-1">
				<div className="flex flex-row gap-2">
					<CustomSelectShadcn
						options={nameTitleOptions}
						inputLabel="Anrede"
						onChange={(value) =>
							setFormData({
								...formData,
								title: value?.toString() || "",
							})
						}
						value={formData.title || ""}
					/>
					<CustomTextFieldShadcn
						label="Vorname"
						name="first_name"
						value={formData.first_name || ""}
						onChange={(event) =>
							setFormData({
								...formData,
								first_name: event.target.value,
							})
						}
					/>
					<CustomTextFieldShadcn
						label="Nachname"
						name="last_name"
						value={formData.last_name || ""}
						onChange={(event) =>
							setFormData({
								...formData,
								last_name: event.target.value,
							})
						}
					/>
				</div>
				<CustomTextFieldShadcn
					label="E-Mail"
					name="email"
					value={formData.email || ""}
					onChange={(event) =>
						setFormData({ ...formData, email: event.target.value })
					}
					validationError={formErrors.email}
				/>
			</div>
			<Button type="submit" className="mt-4">
				Speichern
			</Button>
		</form>
	);
};
